import { Avatar } from "antd";
import axios from "axios";
import {
  ArrowUpDown,
  CalculatorIcon,
  Edit2,
  Eye,
  MegaphoneIcon,
  MoreHorizontalIcon,
  ShieldEllipsisIcon,
  Trash2,
  User2Icon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../lib/api";
import { motion } from "framer-motion";
import SkeletonTable from "./SkeletonTable";
import { DialogDelete } from "../components/Common/DialogDelete";
import { cn } from "../lib/utils";

export default function Users() {
  const navigate = useNavigate();

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const handleFetchUsers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    setLoadingUsers(true);
    handleFetchUsers();
  }, []);

  const getStatusBadge = (role) => {
    switch (role) {
      case "admin":
        return (
          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
            <ShieldEllipsisIcon className="ml-1 h-3 w-3" /> مسؤول
          </Badge>
        );
      case "user":
        return (
          <Badge
            variant="outline"
            className="bg-green-100 text-green-800 hover:bg-green-100"
          >
            <User2Icon className="ml-1 h-3 w-3" /> موظف
          </Badge>
        );
      case "accountant":
        return (
          <Badge
            variant="outline"
            className="bg-blue-100 text-blue-800 hover:bg-blue-100"
          >
            <CalculatorIcon className="ml-1 h-3 w-3" /> محاسب
          </Badge>
        );
      case "marketer":
        return (
          <Badge
            variant="outline"
            className="bg-orange-100 text-orange-800 hover:bg-orange-100"
          >
            <MegaphoneIcon className="ml-1 h-3 w-3" /> مسوق
          </Badge>
        );
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const columns = [
    {
      accessorKey: `user_id.name`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الموظف
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
              "bg-blue-100 text-blue-600",
              "dark:bg-blue-900 dark:text-blue-300"
            )}
            src={`${UrlImage}${row.original.image}`}
          >
            {row.original.name.charAt(0)}
          </Avatar>
          <div className="text-right">
            <div className="font-medium">{row.original.name}</div>
            <div className="text-xs text-muted-foreground">
              {row.original.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "phone",
      header: "الهاتف",
      cell: ({ row }) => row.original.phone || "___",
    },
    {
      accessorKey: "salary",
      header: "الراتب",
      cell: ({ row }) => row.original.salary || "___",
    },
    {
      accessorKey: "work_days",
      header: "أيام العمل",
      cell: ({ row }) => row.original.work_days || "___",
    },
    {
      accessorKey: "work_hours",
      header: "عدد ساعات العمل",
      cell: ({ row }) => row.original.work_hours || "___",
    },
    {
      accessorKey: "work_place",
      header: "مكان العمل",
      cell: ({ row }) => translatWorkPlace(row.original.work_place) || "___",
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الصلاحية
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => getStatusBadge(row.original.role) || "___",
    },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-lg px-2 border-none text-yellow-600"
              onClick={() => navigate(`/users-profile/${row.original.bid}`)}
            >
              <Eye />
            </Button>
            <Button
              size="sm"
              className="rounded-lg px-2 text-green-600"
              variant="ghost"
              onClick={() =>
                navigate(`/users-edit-profile/${row.original.bid}`)
              }
            >
              <Edit2 />
            </Button>
            <DialogDelete
              title={"المستخدم"}
              data={row.original.name}
              apiUrl={`/users/${row.original.bid}`}
              fetchData={handleFetchUsers}
            />
          </div>
        </>
      ),
    },
  ];

  const translateRole = (role) => {
    if (role === "admin") return "مسؤول";
    if (role === "user") return "موظف";
    if (role === "accountant") return "محاسب";
    if (role === "marketer") return "مسوق";
    return role;
  };

  const translatWorkPlace = (work) => {
    if (work === "remote") return "عن بعد";
    if (work === "onsite") return "في المكتب";
    return work;
  };

  const exportData = users.map((row) => ({
    user: row.name || "___",
    phone: row.phone || "___",
    salary: row.salary || "___",
    work_days: row.work_days || "___",
    work_hours: row.work_hours || "___",
    work_place: translatWorkPlace(row.work_place) || "___",
    role: translateRole(row.role) || "___",
  }));

  const exportColumns = [
    { accessorKey: "user", header: "الموظف" },
    { accessorKey: "phone", header: "الهاتف" },
    { accessorKey: "salary", header: "الراتب" },
    { accessorKey: "work_days", header: "أيام العمل" },
    { accessorKey: "work_hours", header: "عدد ساعات العمل" },
    { accessorKey: "work_place", header: "مكان العمل" },
    { accessorKey: "role", header: "الصلاحية" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">المستخدمون</h1>

        <Button onClick={() => navigate(`/users-add-profile`)}>
          إضافة مستخدم
        </Button>
      </div>

      {loadingUsers ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={users}
          title={"المستخدمون"}
          filterNames={["user_id_name", "role"]}
          exportData={exportData}
          exportColumns={exportColumns}
        />
      )}
    </motion.div>
  );
}
